import { z } from 'zod';
import { exportPreferenceFields } from './export-preferences.mjs';
import { deckSlideSchema, DECK_LIMITS } from '../../company-workspace/data/deck-contract.mjs';
import { boundedSchema, id, text, unique } from './common.mjs';
import { documentBlockSchema, documentNodeIds } from './document.mjs';
import { valueSlotSchema } from './value.mjs';

const sectionSchema = z
  .object({ id, title: text(300).optional(), blocks: z.array(documentBlockSchema).max(500) })
  .strict();
export const reportPayloadSchema = boundedSchema(
  z
    .object({
      ...exportPreferenceFields,
      schemaVersion: z.literal(1),
      id,
      title: text(300),
      sections: z.array(sectionSchema).min(1).max(200),
    })
    .strict()
    .superRefine((v, ctx) => {
      unique(
        v.sections.flatMap((s) => [s.id, ...documentNodeIds(s)]),
        ctx,
        ['sections'],
      );
    }),
);
export const propertyPathSchema = z
  .array(id.refine((key) => !['__proto__', 'prototype', 'constructor'].includes(key)))
  .min(1)
  .max(20);
const deckNodeSchema = z
  .object({ id, propertyPath: propertyPathSchema, slot: valueSlotSchema.optional() })
  .strict();
// The existing deck module remains the sole layout and geometry authority.
// Stable slide/node identity wraps its payload; it does not redefine layouts.
export const deckPayloadSchema = boundedSchema(
  z
    .object({
      ...exportPreferenceFields,
      schemaVersion: z.literal(1),
      id,
      title: text(300),
      slides: z
        .array(
          z
            .object({
              id,
              content: deckSlideSchema,
              nodes: z.array(deckNodeSchema).min(1).max(200),
            })
            .strict(),
        )
        .min(1)
        .max(DECK_LIMITS.slides),
    })
    .strict()
    .superRefine((v, ctx) => {
      unique(
        v.slides.flatMap((s) => [s.id, ...s.nodes.map((n) => n.id)]),
        ctx,
        ['slides'],
      );
    }),
);
export const tableColumnSchema = z
  .object({
    id,
    label: text(200),
    type: z.enum(['text', 'integer', 'decimal', 'boolean', 'date', 'timestamp']),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable(),
    unit: text(40).nullable(),
  })
  .strict();
export const tableRowSchema = z.object({ id, cells: z.record(id, valueSlotSchema) }).strict();
export const tablePayloadSchema = boundedSchema(
  z
    .object({
      ...exportPreferenceFields,
      schemaVersion: z.literal(1),
      id,
      title: text(300),
      columns: z.array(tableColumnSchema).min(1).max(100),
      rows: z.array(tableRowSchema),
    })
    .strict()
    .superRefine((v, ctx) => {
      unique(
        v.columns.map((c) => c.id),
        ctx,
        ['columns'],
      );
      unique(
        v.rows.map((r) => r.id),
        ctx,
        ['rows'],
      );
      const keys = new Set(v.columns.map((c) => c.id));
      if (
        v.rows.some(
          (r) =>
            Object.keys(r.cells).length !== keys.size ||
            Object.keys(r.cells).some((k) => !keys.has(k)),
        )
      )
        ctx.addIssue({ code: 'custom', message: 'Rows must contain every declared column' });
    }),
);
export const chartPayloadSchema = boundedSchema(
  z
    .object({
      ...exportPreferenceFields,
      schemaVersion: z.literal(1),
      id,
      title: text(300),
      chartType: z.enum(['bar', 'line', 'column', 'stacked-column']),
      tableRef: id,
      xColumnId: id,
      yColumnIds: z.array(id).min(1).max(12),
    })
    .strict()
    .superRefine((v, ctx) => unique([v.xColumnId, ...v.yColumnIds], ctx, ['yColumnIds'])),
);
export const dashboardPayloadSchema = boundedSchema(
  z
    .object({
      ...exportPreferenceFields,
      schemaVersion: z.literal(1),
      id,
      title: text(300),
      tiles: z
        .array(
          z
            .object({ id, kind: z.enum(['table', 'chart', 'kpi']), registeredPayloadRef: id })
            .strict(),
        )
        .min(1)
        .max(100),
    })
    .strict()
    .superRefine((v, ctx) =>
      unique(
        v.tiles.map((t) => t.id),
        ctx,
        ['tiles'],
      ),
    ),
);
export const kpiPayloadSchema = boundedSchema(
  z
    .object({
      ...exportPreferenceFields,
      schemaVersion: z.literal(1),
      id,
      label: text(200),
      slot: valueSlotSchema,
    })
    .strict(),
);
export const markdownPayloadSchema = boundedSchema(
  z.object({ ...exportPreferenceFields, markdown: text(100000) }).strict(),
);
